import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useParams } from "react-router-dom";
import { BrandWordmark } from "@/components/Brand";
import { ExportMenu } from "@/components/ExportMenu";
import {
  AdminConv,
  AdminConvDetail,
  AdminDoc,
  AdminStats,
  AdminSupport,
  AdminUser,
  NegativeFeedbackItem,
  ReindexHtmlStatus,
  ReindexStatus,
  RuntimeSettings,
  SearchHit,
  api,
} from "@/api/client";
import { useAuthStore } from "@/stores/auth";

export default function AdminPage() {
  return (
    <div className="flex h-screen flex-col bg-bg text-ink">
      <TopNav />
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="settings" element={<SettingsPanel />} />
          <Route path="users" element={<UsersPanel />} />
          <Route path="conversations" element={<ConversationsPanel />} />
          <Route path="conversations/:id" element={<ConversationDetail />} />
          <Route path="documents" element={<DocumentsPanel />} />
          <Route path="support" element={<SupportInboxPanel />} />
          <Route path="logs" element={<LogsPanel />} />
        </Routes>
      </div>
    </div>
  );
}


function TopNav() {
  const { email, displayName, logout } = useAuthStore();
  const tabs = [
    { to: "/admin", label: "Dashboard", end: true },
    { to: "/admin/settings", label: "Settings" },
    { to: "/admin/documents", label: "Documents" },
    { to: "/admin/conversations", label: "Conversations" },
    { to: "/admin/users", label: "Users" },
    { to: "/admin/support", label: "Support" },
    { to: "/admin/logs", label: "Logs" },
  ];
  return (
    <nav className="flex items-center border-b border-bg-border bg-bg-elevated px-5 py-3 text-white">
      <Link to="/admin" className="mr-8">
        <BrandWordmark />
      </Link>
      <div className="flex gap-1">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `rounded-md px-3 py-1.5 text-sm transition ${
                isActive
                  ? "bg-white text-bg"
                  : "text-ink-muted hover:bg-bg-hover hover:text-white"
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
      <div className="ml-auto flex items-center gap-4 text-xs text-ink-muted">
        <Link to="/chat" className="hover:text-white">
          Open chat →
        </Link>
        <Link to="/settings" className="hover:text-white" title={email ?? ""}>
          {displayName || email}
        </Link>
        <button
          onClick={logout}
          className="rounded-md border border-bg-border px-3 py-1 hover:bg-bg-hover hover:text-white"
        >
          Log out
        </button>
      </div>
    </nav>
  );
}

// ───────── Dashboard ─────────

function Dashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [balance, setBalance] = useState<{
    remaining?: number;
    total?: number;
    used?: number;
    err?: string;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api.adminStats().then(setStats).catch((e) => setErr(e.message));
    api
      .adminBalance()
      .then((b) =>
        b.ok
          ? setBalance({
              remaining: b.remaining_usd,
              total: b.total_credits_usd,
              used: b.total_usage_usd,
            })
          : setBalance({ err: b.error })
      )
      .catch((e) => setBalance({ err: (e as Error).message }));
  }, []);
  if (err) return <PageErr msg={err} />;
  if (!stats) return <PageLoading />;

  const fmt$ = (n: number | undefined) =>
    n === undefined ? "—" : `$${n.toFixed(n < 1 ? 4 : 2)}`;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="mb-4 text-xl font-semibold text-white">Dashboard</h1>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card label="Questions today" value={stats.questions.day} />
        <Card label="Questions 7d" value={stats.questions.week} />
        <Card label="Questions 30d" value={stats.questions.month} />
        <Card
          label="👍 / 👎"
          value={`${stats.feedback.positive} / ${stats.feedback.negative}${
            stats.feedback.positive_pct !== null
              ? ` (${stats.feedback.positive_pct}%)`
              : ""
          }`}
        />
        <Card
          label="Context-hit ratio"
          value={stats.context_hit_ratio_pct !== null ? `${stats.context_hit_ratio_pct}%` : "—"}
        />
        <Card
          label="Avg latency"
          value={stats.avg_latency_ms ? `${Math.round(stats.avg_latency_ms)} ms` : "—"}
        />
        <Card label="Avg dialog length" value={stats.avg_dialog_length} />
        <Card
          label="Corpus"
          value={`${stats.corpus.documents} docs / ${stats.corpus.chunks} chunks`}
        />
      </div>

      <h2 className="mt-8 mb-2 text-sm font-semibold text-ink-muted">
        OpenRouter spend
      </h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card
          label="Balance remaining"
          value={
            balance?.err
              ? "—"
              : balance?.remaining !== undefined
              ? fmt$(balance.remaining)
              : "…"
          }
        />
        <Card label="Spent today" value={fmt$(stats.spend_usd.day)} />
        <Card label="Spent 7d" value={fmt$(stats.spend_usd.week)} />
        <Card label="Spent 30d" value={fmt$(stats.spend_usd.month)} />
      </div>
      {balance?.err && (
        <div className="mt-2 text-xs text-red-400">
          Balance poll failed: {balance.err}
        </div>
      )}

      {stats.health && (
        <>
          <h2 className="mt-8 mb-2 text-sm font-semibold text-ink-muted">
            System health
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <HealthTile
              label="PostgreSQL"
              ok={stats.health.db.ok}
              detail={stats.health.db.detail}
            />
            <HealthTile
              label="OpenRouter"
              ok={stats.health.openrouter.ok}
              detail={stats.health.openrouter.ok ? "reachable" : "unreachable"}
            />
            <HealthTile
              label="Embeddings model"
              ok
              detail={stats.health.embedding_model}
            />
            {stats.health.git?.configured ? (
              <HealthTile
                label="Docs git repo"
                ok={Boolean(stats.health.git.is_git_repo)}
                detail={
                  stats.health.git.is_git_repo
                    ? `HEAD ${String(stats.health.git.head || "").slice(0, 7)}`
                    : "not cloned"
                }
              />
            ) : null}
          </div>
        </>
      )}

      <h2 className="mt-8 mb-2 text-sm font-semibold text-ink-muted">Top users (30d)</h2>
      {stats.top_users_30d.length === 0 ? (
        <div className="text-sm text-ink-dim">No activity yet.</div>
      ) : (
        <table className="min-w-full rounded-lg border border-bg-border bg-bg-elevated text-sm overflow-hidden">
          <thead className="bg-bg-card">
            <tr>
              <th className="px-3 py-2 text-left text-ink-muted">Email</th>
              <th className="px-3 py-2 text-right text-ink-muted">Questions</th>
              <th className="px-3 py-2 text-right text-ink-muted">Spent</th>
            </tr>
          </thead>
          <tbody>
            {stats.top_users_30d.map((u) => (
              <tr key={u.email} className="border-t border-bg-border">
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2 text-right">{u.questions}</td>
                <td className="px-3 py-2 text-right text-ink-muted">
                  {fmt$(u.spend_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <NegativeFeedbackPanel />
    </div>
  );
}

// ───────── Negative-feedback list ─────────

function NegativeFeedbackPanel() {
  const [items, setItems] = useState<NegativeFeedbackItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  useEffect(() => {
    api
      .adminNegativeFeedback(100)
      .then(setItems)
      .catch((e) => setErr((e as Error).message));
  }, []);
  if (err) {
    return (
      <div className="mt-8 text-xs text-red-400">
        Could not load negative feedback: {err}
      </div>
    );
  }
  return (
    <>
      <h2 className="mt-8 mb-2 text-sm font-semibold text-ink-muted">
        👎 Disliked answers ({items?.length ?? "…"})
      </h2>
      {items === null ? (
        <div className="text-sm text-ink-dim">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-ink-dim">
          No 👎 yet — users haven't flagged any answer.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const open = expandedId === it.message_id;
            return (
              <div
                key={it.message_id}
                className="rounded-lg border border-bg-border bg-bg-elevated p-3 text-sm"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-white">
                      {it.question || "(no preceding question)"}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-ink-dim">
                      {it.owner_email} ·{" "}
                      {new Date(it.feedback_at).toLocaleString()} ·{" "}
                      <Link
                        to={`/admin/conversations/${it.conversation_id}`}
                        className="underline hover:text-white"
                      >
                        chat #{it.conversation_id}
                      </Link>
                    </div>
                    {it.comment && (
                      <div className="mt-1 text-xs italic text-ink-muted">
                        💬 {it.comment}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      setExpandedId(open ? null : it.message_id)
                    }
                    className="shrink-0 rounded border border-bg-border px-2 py-0.5 text-xs text-ink-dim hover:bg-bg-hover hover:text-white"
                  >
                    {open ? "▼ hide" : "▶ show answer"}
                  </button>
                </div>
                {open && (
                  <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded border border-bg-border bg-bg-card p-3 text-xs text-ink-muted">
                    {it.answer}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function HealthTile({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-bg-border bg-bg-elevated p-4">
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full ${
          ok ? "bg-green-400" : "bg-red-400"
        }`}
      />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-ink-dim">
          {label}
        </div>
        <div
          className={`mt-0.5 truncate text-sm font-medium ${
            ok ? "text-white" : "text-red-400"
          }`}
          title={detail}
        >
          {detail}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-bg-border bg-bg-elevated p-4">
      <div className="text-[11px] uppercase tracking-wide text-ink-dim">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

// ───────── Settings ─────────

function SettingsPanel() {
  const [cfg, setCfg] = useState<RuntimeSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.adminGetSettings().then(setCfg).catch((e) => setErr(e.message));
  }, []);

  async function save() {
    if (!cfg) return;
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      const updated = await api.adminUpdateSettings(cfg);
      setCfg(updated);
      setMsg("Saved. New settings apply to the next request — no restart needed.");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (err && !cfg) return <PageErr msg={err} />;
  if (!cfg) return <PageLoading />;

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-xl font-semibold text-white">Runtime settings</h1>
      <p className="mb-4 text-xs text-ink-muted">
        Changes take effect immediately on the next request. No restart required.
      </p>

      <label className="mb-3 block">
        <div className="mb-1 text-xs font-medium text-ink-muted">System prompt</div>
        <textarea
          value={cfg.system_prompt}
          onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })}
          rows={18}
          className="w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 font-mono text-xs text-white"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <LabelField label="LLM model">
          <input
            className={inputCls}
            value={cfg.llm_model}
            onChange={(e) => setCfg({ ...cfg, llm_model: e.target.value })}
          />
        </LabelField>
        <LabelField label="Fallback model">
          <input
            className={inputCls}
            value={cfg.llm_fallback_model}
            onChange={(e) => setCfg({ ...cfg, llm_fallback_model: e.target.value })}
          />
        </LabelField>
        <LabelField label="Temperature">
          <input
            type="number"
            step={0.05}
            className={inputCls}
            value={cfg.llm_temperature}
            onChange={(e) => setCfg({ ...cfg, llm_temperature: parseFloat(e.target.value) })}
          />
        </LabelField>
        <LabelField label="Top-K chunks">
          <input
            type="number"
            className={inputCls}
            value={cfg.rag_top_k}
            onChange={(e) => setCfg({ ...cfg, rag_top_k: parseInt(e.target.value, 10) })}
          />
        </LabelField>
        <LabelField label="Min score (cosine)">
          <input
            type="number"
            step={0.01}
            className={inputCls}
            value={cfg.rag_min_score}
            onChange={(e) => setCfg({ ...cfg, rag_min_score: parseFloat(e.target.value) })}
          />
        </LabelField>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-bg transition hover:bg-white/90 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        {msg && <span className="text-xs text-green-400">{msg}</span>}
        {err && <span className="text-xs text-red-400">{err}</span>}
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white placeholder:text-ink-dim";

function LabelField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label>
      <div className="mb-1 text-xs font-medium text-ink-muted">{label}</div>
      {children}
    </label>
  );
}

// ───────── Users ─────────

function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [canUseMcp, setCanUseMcp] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function reload() {
    try {
      setUsers(await api.adminListUsers());
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function create() {
    setErr(null);
    try {
      await api.adminCreateUser(
        email,
        password,
        isAdmin,
        displayName.trim() || null,
        canUseMcp
      );
      setEmail("");
      setDisplayName("");
      setPassword("");
      setIsAdmin(false);
      setCanUseMcp(false);
      reload();
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  async function editName(u: AdminUser) {
    const next = prompt(
      `Display name for ${u.email}:`,
      u.display_name ?? ""
    );
    if (next === null) return;
    await api.adminPatchUser(u.id, { display_name: next.trim() || null });
    reload();
  }

  async function toggleAdmin(u: AdminUser) {
    await api.adminPatchUser(u.id, { is_admin: !u.is_admin });
    reload();
  }
  async function toggleActive(u: AdminUser) {
    await api.adminPatchUser(u.id, { is_active: !u.is_active });
    reload();
  }
  async function toggleMcp(u: AdminUser) {
    if (
      !u.can_use_mcp &&
      !confirm(
        `Grant agent (MCP) access to ${u.email}?\n\n` +
          "They will be able to create API keys and search the T24 docs from " +
          "Claude Code, Codex or any MCP client. Documentation excerpts then " +
          "reach that client's model provider."
      )
    )
      return;
    await api.adminPatchUser(u.id, { can_use_mcp: !u.can_use_mcp });
    reload();
  }
  async function resetPassword(u: AdminUser) {
    const p = prompt(`Reset password for ${u.email}:`);
    if (!p) return;
    await api.adminPatchUser(u.id, { password: p });
    alert("Password updated.");
  }
  async function remove(u: AdminUser) {
    if (!confirm(`Delete user ${u.email}?`)) return;
    await api.adminDeleteUser(u.id);
    reload();
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-4 text-xl font-semibold text-white">Users</h1>

      <div className="mb-6 rounded-lg border border-bg-border bg-bg-elevated p-4">
        <div className="mb-2 text-sm font-medium text-white">Create user</div>
        <div className="flex flex-wrap items-end gap-2">
          <input
            placeholder="email@fintegrator.local"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls + " max-w-xs"}
          />
          <input
            placeholder="display name (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputCls + " max-w-xs"}
            maxLength={120}
          />
          <input
            placeholder="password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls + " max-w-xs"}
          />
          <label className="flex items-center gap-1 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
            admin
          </label>
          <label
            className="flex items-center gap-1 text-sm text-ink-muted"
            title="Allows creating API keys and using the docs from an MCP client"
          >
            <input
              type="checkbox"
              checked={canUseMcp}
              onChange={(e) => setCanUseMcp(e.target.checked)}
            />
            MCP
          </label>
          <button
            onClick={create}
            disabled={!email || !password}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-bg transition hover:bg-white/90 disabled:opacity-50"
          >
            Create
          </button>
        </div>
        {err && <div className="mt-2 text-xs text-red-400">{err}</div>}
      </div>

      <table className="min-w-full overflow-hidden rounded-lg border border-bg-border bg-bg-elevated text-sm">
        <thead className="bg-bg-card text-left text-ink-muted">
          <tr>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Display name</th>
            <th className="px-3 py-2">Admin</th>
            <th className="px-3 py-2">Active</th>
            <th className="px-3 py-2" title="Agent access: may create API keys and query the docs over MCP">
              MCP
            </th>
            <th className="px-3 py-2">Created</th>
            <th className="px-3 py-2">Last login</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-bg-border">
              <td className="px-3 py-2 text-white">{u.email}</td>
              <td className="px-3 py-2 text-ink-muted">
                {u.display_name ?? <span className="text-ink-dim">—</span>}
              </td>
              <td className="px-3 py-2">
                <button
                  onClick={() => toggleAdmin(u)}
                  className={`rounded px-2 py-0.5 text-xs ${
                    u.is_admin ? "bg-accent/15 text-accent" : "bg-bg-card text-ink-dim"
                  }`}
                >
                  {u.is_admin ? "yes" : "no"}
                </button>
              </td>
              <td className="px-3 py-2">
                <button
                  onClick={() => toggleActive(u)}
                  className={`rounded px-2 py-0.5 text-xs ${
                    u.is_active ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"
                  }`}
                >
                  {u.is_active ? "active" : "disabled"}
                </button>
              </td>
              <td className="px-3 py-2">
                <button
                  onClick={() => toggleMcp(u)}
                  className={`rounded px-2 py-0.5 text-xs ${
                    u.can_use_mcp
                      ? "bg-accent/15 text-accent"
                      : "bg-bg-card text-ink-dim"
                  }`}
                >
                  {u.can_use_mcp ? "on" : "off"}
                </button>
              </td>
              <td className="px-3 py-2 text-xs text-ink-dim">
                {new Date(u.created_at).toLocaleDateString()}
              </td>
              <td className="px-3 py-2 text-xs text-ink-dim">
                {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => editName(u)} className="mr-2 text-xs text-ink-muted underline hover:text-white">
                  edit name
                </button>
                <button onClick={() => resetPassword(u)} className="mr-2 text-xs text-ink-muted underline hover:text-white">
                  reset password
                </button>
                <button
                  onClick={() => remove(u)}
                  className="text-xs text-red-400 underline"
                >
                  delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────── Conversations ─────────

function ConversationsPanel() {
  const [rows, setRows] = useState<AdminConv[]>([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  useEffect(() => {
    api.adminListConversations().then(setRows);
  }, []);

  // Debounced search.
  useEffect(() => {
    if (q.trim().length < 2) {
      setHits(null);
      setSearchErr(null);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.adminSearchMessages(q.trim(), 100);
        setHits(r);
        setSearchErr(null);
      } catch (e) {
        setSearchErr((e as Error).message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [q]);

  function renderSnippet(text: string, needle: string) {
    if (!needle) return text;
    const idx = text.toLowerCase().indexOf(needle.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="rounded bg-accent/30 px-0.5 text-white">
          {text.slice(idx, idx + needle.length)}
        </mark>
        {text.slice(idx + needle.length)}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-white">All conversations</h1>
        <ExportMenu label="Export all" />
      </div>

      <div className="mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍 Search messages (min 2 chars) — across all team conversations"
          className="w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white placeholder:text-ink-dim"
        />
        {searchErr && (
          <div className="mt-2 text-xs text-red-400">{searchErr}</div>
        )}
      </div>

      {hits !== null ? (
        <div>
          <div className="mb-2 text-xs text-ink-muted">
            {searching
              ? "Searching…"
              : `${hits.length} matching message${hits.length === 1 ? "" : "s"}`}
          </div>
          <div className="space-y-2">
            {hits.map((h) => (
              <Link
                key={h.message_id}
                to={`/admin/conversations/${h.conversation_id}`}
                className="block rounded-lg border border-bg-border bg-bg-elevated p-3 transition hover:border-accent"
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-ink-dim">
                  <span
                    className={`rounded px-1.5 py-0.5 ${
                      h.role === "user"
                        ? "bg-white/10 text-white"
                        : "bg-accent/15 text-accent"
                    }`}
                  >
                    {h.role}
                  </span>
                  <span className="truncate">
                    {h.conv_title || "(untitled)"} · {h.owner_email}
                  </span>
                  <span className="ml-auto">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-white">
                  {renderSnippet(h.snippet, q.trim())}
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <table className="min-w-full overflow-hidden rounded-lg border border-bg-border bg-bg-elevated text-sm">
          <thead className="bg-bg-card text-left text-ink-muted">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2 text-right">Messages</th>
              <th className="px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-bg-border hover:bg-bg-hover">
                <td className="px-3 py-2">
                  <Link
                    className="text-white underline hover:text-accent"
                    to={`/admin/conversations/${r.id}`}
                  >
                    {r.title || "(untitled)"}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs text-ink-dim">{r.user_email}</td>
                <td className="px-3 py-2 text-right">{r.message_count}</td>
                <td className="px-3 py-2 text-xs text-ink-dim">
                  {new Date(r.updated_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ConversationDetail() {
  const { id } = useParams();
  const [data, setData] = useState<AdminConvDetail | null>(null);
  useEffect(() => {
    if (id) api.adminConvDetail(parseInt(id, 10)).then(setData);
  }, [id]);
  if (!data) return <PageLoading />;
  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/conversations"
          className="text-xs underline text-ink-muted hover:text-white"
        >
          ← Back
        </Link>
        <ExportMenu convId={data.id} label="Export" />
      </div>
      <h1 className="mb-1 mt-2 text-xl font-semibold text-white">{data.title}</h1>
      <div className="mb-6 text-xs text-ink-dim">
        Owner: {data.owner_email} · Created {new Date(data.created_at).toLocaleString()}
      </div>
      {data.messages.map((m) => (
        <div key={m.id} className="mb-6 rounded-lg border border-bg-border bg-bg-elevated p-4">
          <div className="mb-1 flex items-center gap-2 text-xs text-ink-muted">
            <span className="rounded bg-bg-card px-2 py-0.5 font-medium text-white">
              {m.role}
            </span>
            <span>{new Date(m.created_at).toLocaleString()}</span>
            {m.feedback && (
              <span
                className={`rounded px-1.5 py-0.5 ${
                  m.feedback.rating === 1
                    ? "bg-green-500/15 text-green-400"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {m.feedback.rating === 1 ? "👍" : "👎"}
              </span>
            )}
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm text-white">{m.content}</pre>
          {m.retrieval && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-ink-muted hover:text-white">Retrieval debug</summary>
              <div className="mt-2 rounded border border-bg-border bg-bg-card p-3 font-mono text-[11px] text-ink-muted">
                <div>
                  <b className="text-white">query:</b> {m.retrieval.query}
                </div>
                <div>
                  <b className="text-white">rewritten:</b> {m.retrieval.rewritten_query ?? "(same)"}
                </div>
                <div>
                  <b className="text-white">had_context:</b> {String(m.retrieval.had_context)}
                </div>
                <div>
                  <b className="text-white">model:</b> {m.retrieval.model} · tokens in/out:{" "}
                  {m.retrieval.tokens_in}/{m.retrieval.tokens_out} · latency:{" "}
                  {m.retrieval.latency_ms ? Math.round(m.retrieval.latency_ms) : "—"} ms
                </div>
                <div>
                  <b className="text-white">chunk_ids:</b>{" "}
                  {(m.retrieval.retrieved_chunk_ids ?? []).join(", ") || "(none)"}
                </div>
                <div>
                  <b className="text-white">scores:</b>{" "}
                  {(m.retrieval.scores ?? []).map((s) => s.toFixed(3)).join(", ") || "(none)"}
                </div>
              </div>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

// ───────── Documents ─────────

interface GitStatus {
  configured: boolean;
  url: string;
  branch: string;
  key_available: boolean;
  is_git_repo: boolean;
  head: string | null;
  last_commit_at: string | null;
  last_commit_msg: string | null;
}

function DocumentsPanel() {
  const [docs, setDocs] = useState<AdminDoc[]>([]);
  const [reindex, setReindex] = useState<ReindexStatus | null>(null);
  const [reindexHtml, setReindexHtml] = useState<ReindexHtmlStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyHtml, setBusyHtml] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [gitStatus, setGitStatus] = useState<GitStatus | null>(null);
  const [gitBusy, setGitBusy] = useState(false);
  const [gitMsg, setGitMsg] = useState<string | null>(null);
  const [gitErr, setGitErr] = useState<string | null>(null);

  async function reload() {
    setDocs(await api.adminListDocuments());
    setReindex(await api.adminReindexStatus());
    try {
      setReindexHtml(await api.adminReindexHtmlStatus());
    } catch {
      // older backend without /reindex/html — silently ignore
    }
    try {
      setGitStatus(await api.adminGitStatus());
    } catch {
      // admin endpoint may be absent in older backend
    }
  }
  useEffect(() => {
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, []);

  async function runReindex() {
    setBusy(true);
    try {
      await api.adminReindex();
      await reload();
    } finally {
      setBusy(false);
    }
  }

  async function runReindexHtml() {
    setBusyHtml(true);
    try {
      await api.adminReindexHtml();
      await reload();
    } finally {
      setBusyHtml(false);
    }
  }

  async function handleFiles(files: FileList | File[]) {
    setUploadMsg(null);
    setUploadErr(null);
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      let total = 0;
      for (const f of arr) {
        const r = await api.adminUploadDocument(f);
        total += r.chunks;
        setUploadMsg(`Uploaded ${r.filename} — ${r.chunks} chunks`);
      }
      if (arr.length > 1) {
        setUploadMsg(`Uploaded ${arr.length} files · ${total} chunks total`);
      }
      await reload();
    } catch (e) {
      setUploadErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function removeDoc(id: number, path: string) {
    if (!confirm(`Delete "${path}" and all its chunks?`)) return;
    try {
      await api.adminDeleteDocument(id);
      await reload();
    } catch (e) {
      setUploadErr((e as Error).message);
    }
  }

  async function runGitPull() {
    setGitBusy(true);
    setGitMsg(null);
    setGitErr(null);
    try {
      const r = await api.adminGitPull();
      if (r.changed_files.length === 0) {
        setGitMsg("Already up to date.");
      } else {
        setGitMsg(
          `${r.action === "clone" ? "Cloned" : "Pulled"} — ${r.changed_files.length} file(s) changed. Indexer will pick them up within ~3s.`
        );
      }
      await reload();
    } catch (e) {
      setGitErr((e as Error).message);
    } finally {
      setGitBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Documents</h1>
        <div className="flex gap-2">
          <button
            onClick={runReindex}
            disabled={busy || reindex?.running}
            className="rounded-md border border-bg-border bg-bg-elevated px-3 py-2 text-sm font-medium text-white transition hover:bg-bg-hover disabled:opacity-50"
          >
            {reindex?.running ? "Reindexing..." : "Reindex all"}
          </button>
          <button
            onClick={runReindexHtml}
            disabled={busyHtml || reindexHtml?.running}
            className="rounded-md border border-bg-border bg-bg-elevated px-3 py-2 text-sm font-medium text-white transition hover:bg-bg-hover disabled:opacity-50"
            title="Re-walk the external HTML doc corpus (HTML_DOCS_PATH)"
          >
            {reindexHtml?.running ? "Reindexing HTML..." : "Reindex HTML"}
          </button>
        </div>
      </div>

      {/* Git integration panel */}
      {gitStatus?.configured && (
        <div className="mb-4 rounded-xl border border-bg-border bg-bg-elevated p-4 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-medium text-white">Git documentation repo</div>
            <button
              onClick={runGitPull}
              disabled={gitBusy || !gitStatus.key_available}
              className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-bg transition hover:bg-white/90 disabled:opacity-50"
            >
              {gitBusy
                ? "Working…"
                : gitStatus.is_git_repo
                ? "Pull latest"
                : "Clone repo"}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-1 text-xs text-ink-muted sm:grid-cols-2">
            <div>
              <span className="text-ink-dim">URL:</span>{" "}
              <span className="font-mono text-white">{gitStatus.url}</span>
            </div>
            <div>
              <span className="text-ink-dim">Branch:</span>{" "}
              <span className="font-mono text-white">{gitStatus.branch}</span>
            </div>
            <div>
              <span className="text-ink-dim">SSH key:</span>{" "}
              {gitStatus.key_available ? (
                <span className="text-green-400">available</span>
              ) : (
                <span className="text-red-400">missing</span>
              )}
            </div>
            <div>
              <span className="text-ink-dim">Cloned:</span>{" "}
              {gitStatus.is_git_repo ? (
                <span className="text-green-400">yes</span>
              ) : (
                <span className="text-ink-dim">not yet</span>
              )}
            </div>
            {gitStatus.head && (
              <>
                <div>
                  <span className="text-ink-dim">HEAD:</span>{" "}
                  <span className="font-mono text-white">{gitStatus.head}</span>
                </div>
                <div>
                  <span className="text-ink-dim">Last commit:</span>{" "}
                  <span className="text-white">
                    {gitStatus.last_commit_at
                      ? new Date(gitStatus.last_commit_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
                {gitStatus.last_commit_msg && (
                  <div className="sm:col-span-2">
                    <span className="text-ink-dim">Message:</span>{" "}
                    <span className="text-white">
                      "{gitStatus.last_commit_msg}"
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          {gitMsg && <div className="mt-2 text-xs text-green-400">{gitMsg}</div>}
          {gitErr && <div className="mt-2 text-xs text-red-400">{gitErr}</div>}
        </div>
      )}

      {/* Upload zone */}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
        }}
        className={`mb-4 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed p-8 transition ${
          dragOver
            ? "border-accent bg-accent/5"
            : "border-bg-border bg-bg-elevated/40 hover:bg-bg-elevated/70"
        }`}
      >
        <input
          type="file"
          accept=".pdf,.md,.markdown,.html,.htm,.txt"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="text-sm text-white">
          {uploading ? (
            <>Uploading…</>
          ) : (
            <>📎 <b>Drop a document here</b> — or click to choose</>
          )}
        </div>
        <div className="text-xs text-ink-dim">
          PDF · Markdown · HTML · TXT · up to 50 MB
        </div>
        {uploadMsg && (
          <div className="mt-2 text-xs text-green-400">{uploadMsg}</div>
        )}
        {uploadErr && (
          <div className="mt-2 text-xs text-red-400">{uploadErr}</div>
        )}
      </label>

      {reindex && (
        <div className="mb-4 rounded-md border border-bg-border bg-bg-elevated p-3 text-xs text-ink-muted">
          <div>
            <b className="text-white">PDF/MD/TXT reindex</b> · status:{" "}
            <b className="text-white">{reindex.running ? "running" : reindex.finished_at ? "idle" : "never run"}</b>
            {reindex.started_at && (
              <> · started {new Date(reindex.started_at).toLocaleTimeString()}</>
            )}
            {reindex.finished_at && (
              <> · finished {new Date(reindex.finished_at).toLocaleTimeString()}</>
            )}
          </div>
          {reindex.error && (
            <div className="mt-1 text-red-400">error: {reindex.error}</div>
          )}
          {reindex.result && (
            <div className="mt-1">
              added {reindex.result.added.length} · updated{" "}
              {reindex.result.updated.length} · unchanged{" "}
              {reindex.result.unchanged.length} · removed{" "}
              {reindex.result.removed.length} · total chunks written{" "}
              {reindex.result.total_chunks}
            </div>
          )}
        </div>
      )}

      {reindexHtml && (reindexHtml.running || reindexHtml.started_at) && (
        <div className="mb-4 rounded-md border border-bg-border bg-bg-elevated p-3 text-xs text-ink-muted">
          <div>
            <b className="text-white">HTML reindex</b> · status:{" "}
            <b className="text-white">
              {reindexHtml.running
                ? "running"
                : reindexHtml.finished_at
                  ? "idle"
                  : "never run"}
            </b>
            {reindexHtml.started_at && (
              <> · started {new Date(reindexHtml.started_at).toLocaleTimeString()}</>
            )}
            {reindexHtml.finished_at && (
              <> · finished {new Date(reindexHtml.finished_at).toLocaleTimeString()}</>
            )}
          </div>
          {(() => {
            const p = reindexHtml.progress;
            if (!p || p.total === 0) return null;
            const pct = Math.min(100, Math.round((p.processed / p.total) * 100));
            return (
              <div className="mt-2">
                <div className="mb-1 flex justify-between text-xs">
                  <span>
                    {p.processed.toLocaleString()} / {p.total.toLocaleString()} files
                    · {p.chunks.toLocaleString()} chunks
                  </span>
                  <span className="font-mono text-white">{pct}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-bg-border">
                  <div
                    className="h-full bg-accent transition-[width] duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })()}
          {reindexHtml.error && (
            <div className="mt-1 text-red-400">error: {reindexHtml.error}</div>
          )}
          {reindexHtml.result && !reindexHtml.running && (
            <div className="mt-1">
              added {reindexHtml.result.added} · updated{" "}
              {reindexHtml.result.updated} · unchanged{" "}
              {reindexHtml.result.unchanged} · removed{" "}
              {reindexHtml.result.removed} · total chunks written{" "}
              {reindexHtml.result.total_chunks}
            </div>
          )}
        </div>
      )}

      <table className="min-w-full overflow-hidden rounded-lg border border-bg-border bg-bg-elevated text-sm">
        <thead className="bg-bg-card text-left text-ink-muted">
          <tr>
            <th className="px-3 py-2">Path</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2 text-right">Chunks</th>
            <th className="px-3 py-2">Indexed at</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-t border-bg-border">
              <td className="px-3 py-2 font-mono text-xs text-white">{d.path}</td>
              <td className="px-3 py-2">{d.source_type}</td>
              <td className="px-3 py-2 text-right">{d.chunk_count}</td>
              <td className="px-3 py-2 text-xs text-ink-dim">
                {new Date(d.indexed_at).toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => removeDoc(d.id, d.path)}
                  className="text-xs text-red-400 underline hover:text-red-300"
                >
                  delete
                </button>
              </td>
            </tr>
          ))}
          {docs.length === 0 && (
            <tr>
              <td colSpan={5} className="px-3 py-8 text-center text-xs text-ink-dim">
                No documents yet. Drop a file above to add one.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ───────── Logs ─────────

interface LogEntry {
  asctime?: string;
  levelname?: string;
  name?: string;
  message?: string;
  [k: string]: unknown;
}

function LogsPanel() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [level, setLevel] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [stats, setStats] = useState<{ returned: number; sizeKb: number } | null>(null);

  async function reload() {
    try {
      setErr(null);
      const r = await api.adminLogs({ limit: 300, level: level || undefined, q: q || undefined });
      setEntries(r.entries as LogEntry[]);
      setStats({ returned: r.returned, sizeKb: Math.round(r.file_size_bytes / 1024) });
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    reload();
    if (!autoRefresh) return;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [level, q, autoRefresh]);

  function levelBadge(lv?: string) {
    const cls: Record<string, string> = {
      ERROR: "bg-red-500/15 text-red-400",
      CRITICAL: "bg-red-500/15 text-red-400",
      WARNING: "bg-yellow-500/15 text-yellow-400",
      INFO: "bg-bg-card text-ink-muted",
      DEBUG: "bg-bg-card text-ink-dim",
    };
    return cls[lv || "INFO"] || cls.INFO;
  }

  function fmtTime(ts?: string) {
    if (!ts) return "";
    // logger produces "YYYY-MM-DD HH:MM:SS,mmm"
    return ts.replace(",", ".").slice(5); // drop the year for a tight column
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Logs</h1>
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          {stats && (
            <span>
              {stats.returned} entries · file {stats.sizeKb} KB
            </span>
          )}
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            auto-refresh
          </label>
          <button
            onClick={reload}
            className="rounded-md border border-bg-border px-2 py-1 hover:bg-bg-hover"
          >
            ↻
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="rounded-md border border-bg-border bg-bg-card px-2 py-1 text-xs text-white"
        >
          <option value="">all levels</option>
          <option value="ERROR">ERROR</option>
          <option value="WARNING">WARNING</option>
          <option value="INFO">INFO</option>
          <option value="DEBUG">DEBUG</option>
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search… (path, user, error)"
          className="flex-1 rounded-md border border-bg-border bg-bg-card px-3 py-1 text-xs text-white placeholder:text-ink-dim"
        />
      </div>

      {err && (
        <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {err}
        </div>
      )}

      <div className="rounded-lg border border-bg-border bg-bg-elevated font-mono text-[11px]">
        {entries.length === 0 && (
          <div className="p-6 text-center text-ink-dim">No log entries match.</div>
        )}
        {entries.map((e, i) => {
          // Strip standard fields from the "extra" payload for a clean details view.
          const base = new Set(["asctime", "levelname", "name", "message"]);
          const extra: Record<string, unknown> = {};
          for (const k of Object.keys(e)) {
            if (!base.has(k)) extra[k] = e[k];
          }
          return (
            <div
              key={i}
              className="flex gap-2 border-t border-bg-border/60 px-3 py-1.5 first:border-t-0 hover:bg-bg-hover/40"
            >
              <span className="whitespace-nowrap text-ink-dim">{fmtTime(e.asctime)}</span>
              <span
                className={`whitespace-nowrap rounded px-1.5 py-[1px] ${levelBadge(
                  e.levelname
                )}`}
              >
                {e.levelname}
              </span>
              <span className="whitespace-nowrap text-ink-muted">{e.name}</span>
              <span className="min-w-0 flex-1 truncate text-white" title={e.message || ""}>
                {e.message}
              </span>
              {Object.keys(extra).length > 0 && (
                <details className="text-ink-muted">
                  <summary className="cursor-pointer select-none">…</summary>
                  <pre className="mt-1 whitespace-pre-wrap rounded bg-bg-card p-2 text-[10px]">
                    {JSON.stringify(extra, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Support inbox ─────────

function SupportInboxPanel() {
  const [filter, setFilter] = useState<"open" | "resolved" | "all">("open");
  const [items, setItems] = useState<AdminSupport[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  function load() {
    api
      .adminListSupport(filter === "all" ? undefined : filter)
      .then(setItems)
      .catch((e) => setErr((e as Error).message));
  }
  useEffect(load, [filter]);

  async function flip(id: number, next: "open" | "resolved") {
    setBusy(id);
    try {
      await api.adminPatchSupport(id, next);
      load();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Support inbox</h2>
        <div className="flex gap-1 text-xs">
          {(["open", "resolved", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-md px-3 py-1 ${
                filter === s
                  ? "bg-white text-bg"
                  : "border border-bg-border text-ink-muted hover:bg-bg-hover hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {err && <PageErr msg={err} />}
      {!items ? (
        <PageLoading />
      ) : items.length === 0 ? (
        <div className="rounded border border-bg-border bg-bg-elevated p-6 text-sm text-ink-dim">
          No messages.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((m) => (
            <li
              key={m.id}
              className="rounded-lg border border-bg-border bg-bg-elevated p-4"
            >
              <div className="mb-1 flex items-center gap-3 text-xs">
                <span className="font-medium text-white">
                  {m.email_snapshot}
                </span>
                <span className="text-ink-dim">
                  {new Date(m.created_at).toLocaleString()}
                </span>
                <span
                  className={
                    m.status === "resolved"
                      ? "ml-auto text-emerald-400"
                      : "ml-auto text-amber-400"
                  }
                >
                  {m.status}
                </span>
              </div>
              <div className="whitespace-pre-wrap text-sm text-ink">{m.body}</div>
              <div className="mt-3 flex items-center justify-between text-xs text-ink-dim">
                <span>
                  {m.resolved_at
                    ? `Resolved ${new Date(
                        m.resolved_at
                      ).toLocaleString()} by ${m.resolved_by_email ?? "—"}`
                    : ""}
                </span>
                <button
                  disabled={busy === m.id}
                  onClick={() =>
                    flip(m.id, m.status === "open" ? "resolved" : "open")
                  }
                  className="rounded-md border border-bg-border px-3 py-1 text-white hover:bg-bg-hover disabled:opacity-60"
                >
                  {busy === m.id
                    ? "…"
                    : m.status === "open"
                    ? "Mark resolved"
                    : "Reopen"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ───────── helpers ─────────

function PageLoading() {
  return <div className="p-6 text-sm text-ink-dim">Loading...</div>;
}
function PageErr({ msg }: { msg: string }) {
  return <div className="p-6 text-sm text-red-400">Error: {msg}</div>;
}
